import { useEffect, useMemo, useState } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
} from "recharts";
import {
    Calendar,
    CalendarCheck,
    CheckCircle2,
    XCircle,
    IndianRupee,
    Download,
    TrendingUp,
    Loader2,
    AlertCircle,
    RotateCcw,
} from "lucide-react";
import { getExpertBookings } from "../service/courseService.jsx";

// Change this if expertId should come from auth/context instead of being fixed.
const EXPERT_ID = 2;

const CLOSED_STATUSES = new Set(["COMPLETED"]);
const CANCELLED_STATUSES = new Set(["CANCELLED", "REJECTED"]);
// Everything else (CONFIRMED, PENDING, etc.) is treated as "active".

const STATUS_COLORS = {
    Active: "#4f46e5",
    Closed: "#10b981",
    Cancelled: "#f43f5e",
};

const DATE_TABS = [
    { key: "all", label: "All time" },
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "month", label: "This month" },
];

function formatINR(n) {
    return `₹${(Number(n) || 0).toLocaleString("en-IN")}`;
}

function toDateOnly(d) {
    return d.toISOString().slice(0, 10);
}

function todayStr() {
    return toDateOnly(new Date());
}

function yesterdayStr() {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return toDateOnly(d);
}

// ---------------------------------------------------------------------------
// Turn raw booking records into a continuous daily series (no gaps), each
// day carrying active/closed/cancelled counts + revenue from closed sessions.
// ---------------------------------------------------------------------------
function buildDailySeries(bookings) {
    if (bookings.length === 0) return [];

    const byDate = new Map(); // "YYYY-MM-DD" -> { active, closed, cancelled, revenue }

    let minDate = null;
    let maxDate = null;

    bookings.forEach((b) => {
        const dateIso = b.bookingDate;
        if (!dateIso) return;

        if (!minDate || dateIso < minDate) minDate = dateIso;
        if (!maxDate || dateIso > maxDate) maxDate = dateIso;

        const status = (b.bookingStatus || "").toUpperCase();
        const amount = Number(b.totalAmount ?? b.consultationFee) || 0;

        if (!byDate.has(dateIso)) {
            byDate.set(dateIso, { active: 0, closed: 0, cancelled: 0, revenue: 0 });
        }
        const entry = byDate.get(dateIso);

        if (CLOSED_STATUSES.has(status)) {
            entry.closed += 1;
            entry.revenue += amount;
        } else if (CANCELLED_STATUSES.has(status)) {
            entry.cancelled += 1;
        } else {
            entry.active += 1;
        }
    });

    if (!minDate || !maxDate) return [];

    // Extend the range to include today, so "upcoming" bookings scheduled
    // ahead don't get cut off, and recent empty days still show as 0.
    const today = todayStr();
    if (today > maxDate) maxDate = today;
    if (today < minDate) minDate = today;

    // Fill every day in [minDate, maxDate] so charts render a continuous line,
    // even for days with zero bookings.
    const rows = [];
    const cursor = new Date(`${minDate}T00:00:00`);
    const end = new Date(`${maxDate}T00:00:00`);

    while (cursor <= end) {
        const dateIso = toDateOnly(cursor);
        const entry = byDate.get(dateIso) || { active: 0, closed: 0, cancelled: 0, revenue: 0 };
        rows.push({
            date: dateIso,
            label: cursor.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
            ...entry,
        });
        cursor.setDate(cursor.getDate() + 1);
    }

    return rows;
}

// ---------------------------------------------------------------------------
// Summary card
// ---------------------------------------------------------------------------
function StatCard({ icon: Icon, label, value, accent, sub }) {
    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p>
                <div className={`flex h-8 w-8 items-center justify-center rounded-lg ${accent.bg}`}>
                    <Icon className={`h-4 w-4 ${accent.text}`} />
                </div>
            </div>
            <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
            {sub && <p className="mt-0.5 text-xs text-slate-400">{sub}</p>}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function ReportsAnalytics() {
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);

    // Date filter — today / yesterday / this month / all time / custom range
    const [dateFilter, setDateFilter] = useState("all");
    const [showCustom, setShowCustom] = useState(false);
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");

    const loadBookings = async () => {
        try {
            setLoading(true);
            setLoadError(null);
            const response = await getExpertBookings(EXPERT_ID);
            const list = Array.isArray(response) ? response : response?.data || [];
            setBookings(list);
        } catch (err) {
            console.error("Failed to fetch bookings:", err);
            setLoadError(err.message || "Failed to load report data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBookings();
    }, []);

    const dailyData = useMemo(() => buildDailySeries(bookings), [bookings]);

    const minDate = dailyData.length ? dailyData[0].date : "";
    const maxDate = dailyData.length ? dailyData[dailyData.length - 1].date : "";
    const hasData = dailyData.length > 0;

    const filtered = useMemo(() => {
        if (dateFilter === "today") {
            const t = todayStr();
            return dailyData.filter((r) => r.date === t);
        }
        if (dateFilter === "yesterday") {
            const y = yesterdayStr();
            return dailyData.filter((r) => r.date === y);
        }
        if (dateFilter === "month") {
            const now = new Date();
            return dailyData.filter((r) => {
                const d = new Date(`${r.date}T00:00:00`);
                return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
            });
        }
        if (dateFilter === "custom") {
            if (!customFrom && !customTo) return dailyData;
            return dailyData.filter((r) => {
                if (customFrom && r.date < customFrom) return false;
                if (customTo && r.date > customTo) return false;
                return true;
            });
        }
        return dailyData; // "all"
    }, [dailyData, dateFilter, customFrom, customTo]);

    const rangeLabel = useMemo(() => {
        if (dateFilter === "custom") {
            if (customFrom || customTo) return `${customFrom || "…"} → ${customTo || "…"}`;
            return "Custom range";
        }
        if (filtered.length) return `${filtered[0].date} to ${filtered[filtered.length - 1].date}`;
        return DATE_TABS.find((d) => d.key === dateFilter)?.label || "";
    }, [dateFilter, customFrom, customTo, filtered]);

    const totals = useMemo(() => {
        const active = filtered.reduce((s, r) => s + r.active, 0);
        const closed = filtered.reduce((s, r) => s + r.closed, 0);
        const cancelled = filtered.reduce((s, r) => s + r.cancelled, 0);
        const revenue = filtered.reduce((s, r) => s + r.revenue, 0);
        return { active, closed, cancelled, revenue, total: active + closed + cancelled };
    }, [filtered]);

    const pieData = [
        { name: "Active", value: totals.active },
        { name: "Closed", value: totals.closed },
        { name: "Cancelled", value: totals.cancelled },
    ];

    const exportCSV = () => {
        const headers = ["Date", "Active", "Closed", "Cancelled", "Revenue (INR)"];
        const lines = filtered.map((r) => [r.date, r.active, r.closed, r.cancelled, r.revenue].join(","));
        const csv = [headers.join(","), ...lines].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `bookings-report-${dateFilter}-${todayStr()}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="h-screen w-full overflow-y-auto bg-slate-50 p-3 sm:p-5">
            <div className="mx-auto w-full max-w-[1600px]">
                {/* Top bar: date filters + export */}
                <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="mr-1 flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                                <Calendar className="h-3.5 w-3.5 text-slate-400" />
                                Date
                            </span>
                            {DATE_TABS.map((d) => (
                                <button
                                    key={d.key}
                                    onClick={() => {
                                        setDateFilter(d.key);
                                        setShowCustom(false);
                                    }}
                                    disabled={!hasData || loading}
                                    className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${dateFilter === d.key
                                        ? "border-indigo-600 bg-indigo-50 text-indigo-600"
                                        : "border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-600"
                                        }`}
                                >
                                    {d.label}
                                </button>
                            ))}
                            <button
                                onClick={() => {
                                    setDateFilter("custom");
                                    setShowCustom((s) => !s);
                                }}
                                disabled={!hasData || loading}
                                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${dateFilter === "custom"
                                    ? "border-indigo-600 bg-indigo-50 text-indigo-600"
                                    : "border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-600"
                                    }`}
                            >
                                Custom date{dateFilter === "custom" ? ` · ${rangeLabel}` : ""}
                            </button>
                            {dateFilter !== "all" && (
                                <button
                                    onClick={() => {
                                        setDateFilter("all");
                                        setShowCustom(false);
                                        setCustomFrom("");
                                        setCustomTo("");
                                    }}
                                    className="ml-1 text-xs font-medium text-slate-400 hover:text-slate-600"
                                >
                                    Clear
                                </button>
                            )}
                        </div>

                        <div className="flex gap-2 self-start sm:self-auto">
                            <button
                                onClick={loadBookings}
                                disabled={loading}
                                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:border-indigo-300 hover:text-indigo-600 transition-colors disabled:opacity-50"
                            >
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                                Refresh
                            </button>
                            <button
                                onClick={exportCSV}
                                disabled={!hasData}
                                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors disabled:opacity-50"
                            >
                                <Download className="h-4 w-4" />
                                Export
                            </button>
                        </div>
                    </div>

                    {showCustom && dateFilter === "custom" && (
                        <div className="mt-3 flex flex-wrap items-end gap-3 border-t border-slate-100 pt-3">
                            <div>
                                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                    From
                                </label>
                                <input
                                    type="date"
                                    value={customFrom}
                                    max={customTo || undefined}
                                    onChange={(e) => setCustomFrom(e.target.value)}
                                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                            </div>
                            <span className="pb-2 text-sm text-slate-400">to</span>
                            <div>
                                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                                    To
                                </label>
                                <input
                                    type="date"
                                    value={customTo}
                                    min={customFrom || undefined}
                                    onChange={(e) => setCustomTo(e.target.value)}
                                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                            </div>
                            <button
                                onClick={() => setShowCustom(false)}
                                className="rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
                            >
                                Apply
                            </button>
                        </div>
                    )}
                </div>

                {loadError && (
                    <div className="mb-4 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                        <AlertCircle size={20} />
                        <span>{loadError}</span>
                    </div>
                )}

                {loading && (
                    <div className="mb-4 flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white p-10 text-slate-400">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Loading report data...
                    </div>
                )}

                {!loading && !loadError && !hasData && (
                    <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">
                        No bookings found for this expert yet — the report will populate once bookings come in.
                    </div>
                )}

                {!loading && hasData && (
                    <>
                        {/* Summary cards */}
                        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                            <StatCard
                                icon={CalendarCheck}
                                label="Active bookings"
                                value={totals.active}
                                accent={{ bg: "bg-indigo-50", text: "text-indigo-600" }}
                                sub={rangeLabel}
                            />
                            <StatCard
                                icon={CheckCircle2}
                                label="Closed bookings"
                                value={totals.closed}
                                accent={{ bg: "bg-emerald-50", text: "text-emerald-600" }}
                                sub="Completed sessions"
                            />
                            <StatCard
                                icon={XCircle}
                                label="Cancelled"
                                value={totals.cancelled}
                                accent={{ bg: "bg-rose-50", text: "text-rose-600" }}
                                sub="Dropped / rejected bookings"
                            />
                            <StatCard
                                icon={IndianRupee}
                                label="Revenue"
                                value={formatINR(totals.revenue)}
                                accent={{ bg: "bg-violet-50", text: "text-violet-600" }}
                                sub={`From ${totals.closed} closed sessions`}
                            />
                        </div>

                        {/* Charts row */}
                        <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
                            {/* Daily bookings bar chart */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4 lg:col-span-2">
                                <div className="mb-3 flex items-center justify-between">
                                    <p className="text-sm font-semibold text-slate-900">Daily bookings</p>
                                    <p className="text-xs text-slate-400">Active vs Closed vs Cancelled</p>
                                </div>
                                {filtered.length === 0 ? (
                                    <div className="flex h-[280px] items-center justify-center text-sm text-slate-400">
                                        No bookings in this range.
                                    </div>
                                ) : (
                                    <ResponsiveContainer width="100%" height={280}>
                                        <BarChart data={filtered} barGap={2}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                            <XAxis
                                                dataKey="label"
                                                tick={{ fontSize: 11, fill: "#94a3b8" }}
                                                interval={Math.max(0, Math.floor(filtered.length / 10))}
                                                axisLine={{ stroke: "#e2e8f0" }}
                                                tickLine={false}
                                            />
                                            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
                                            <Tooltip
                                                contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                                            />
                                            <Legend wrapperStyle={{ fontSize: 12 }} />
                                            <Bar dataKey="active" name="Active" fill={STATUS_COLORS.Active} radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="closed" name="Closed" fill={STATUS_COLORS.Closed} radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="cancelled" name="Cancelled" fill={STATUS_COLORS.Cancelled} radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </div>

                            {/* Status distribution donut */}
                            <div className="rounded-2xl border border-slate-200 bg-white p-4">
                                <p className="mb-3 text-sm font-semibold text-slate-900">Status distribution</p>
                                {totals.total === 0 ? (
                                    <div className="flex h-[220px] items-center justify-center text-sm text-slate-400">
                                        No bookings in this range.
                                    </div>
                                ) : (
                                    <>
                                        <ResponsiveContainer width="100%" height={220}>
                                            <PieChart>
                                                <Pie
                                                    data={pieData}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    innerRadius={55}
                                                    outerRadius={85}
                                                    paddingAngle={3}
                                                >
                                                    {pieData.map((entry) => (
                                                        <Cell key={entry.name} fill={STATUS_COLORS[entry.name]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                        <div className="mt-2 space-y-2">
                                            {pieData.map((entry) => (
                                                <div key={entry.name} className="flex items-center justify-between text-sm">
                                                    <span className="flex items-center gap-2 text-slate-600">
                                                        <span
                                                            className="h-2.5 w-2.5 rounded-full"
                                                            style={{ backgroundColor: STATUS_COLORS[entry.name] }}
                                                        />
                                                        {entry.name}
                                                    </span>
                                                    <span className="font-medium text-slate-900">
                                                        {entry.value}
                                                        <span className="ml-1 text-xs text-slate-400">
                                                            ({totals.total ? Math.round((entry.value / totals.total) * 100) : 0}%)
                                                        </span>
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Revenue trend */}
                        <div className="rounded-2xl border border-slate-200 bg-white p-4">
                            <div className="mb-3 flex items-center justify-between">
                                <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                                    <TrendingUp className="h-4 w-4 text-indigo-600" />
                                    Revenue trend
                                </p>
                                <p className="text-xs text-slate-400">Daily revenue from closed sessions</p>
                            </div>
                            {filtered.length === 0 ? (
                                <div className="flex h-[220px] items-center justify-center text-sm text-slate-400">
                                    No bookings in this range.
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={220}>
                                    <LineChart data={filtered}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis
                                            dataKey="label"
                                            tick={{ fontSize: 11, fill: "#94a3b8" }}
                                            interval={Math.max(0, Math.floor(filtered.length / 10))}
                                            axisLine={{ stroke: "#e2e8f0" }}
                                            tickLine={false}
                                        />
                                        <YAxis
                                            tick={{ fontSize: 11, fill: "#94a3b8" }}
                                            axisLine={false}
                                            tickLine={false}
                                            tickFormatter={(v) => `₹${v / 1000}k`}
                                        />
                                        <Tooltip
                                            formatter={(v) => formatINR(v)}
                                            contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="revenue"
                                            name="Revenue"
                                            stroke="#7c3aed"
                                            strokeWidth={2.5}
                                            dot={false}
                                            activeDot={{ r: 5 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}